import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Avatar
} from '@mui/material';
import { Carrot, Info } from '@phosphor-icons/react';
import useThemeStyles from '../../theme/useThemeStyles';
import api from '../../utils/api';

const planningRoles = [
  { value: 'lead', label: 'Lead Planner (Primary Decision Maker)' },
  { value: 'co-lead', label: 'Co-Lead (Equal Decision Making)' },
  { value: 'support', label: 'Support Role (Assists with Decisions)' },
  { value: 'delegate', label: 'Delegate (Handles Specific Areas)' },
  { value: 'consultant', label: 'Consultant (Provides Input Only)' }
];

const relationships = [
  'Parent',
  'Sibling',
  'Best Friend',
  'Wedding Party Member',
  'Wedding Planner',
  'Other'
];

function CoupleMemberGuestModal({ open, onClose, guest, onSaved }) {
  const styles = useThemeStyles();
  const partnerRef = guest?.partnerRef;
  const [formData, setFormData] = useState({
    fullName: '',
    preferredName: '',
    profilePicture: '',
    planningRole: '',
    mainWeddingDayContact: false,
    emergencyContact: { name: '', phone: '', relationship: '' },
    dietaryPreferences: '',
    allergies: '',
    accessibilityNeeds: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open && partnerRef) {
      fetchPartnerDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, partnerRef]);

  const fetchPartnerDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/wedding-details/partners');
      const partner = response.data?.[partnerRef] || {};
      setFormData({
        fullName: partner.fullName || '',
        preferredName: partner.preferredName || '',
        profilePicture: partner.profilePicture || '',
        planningRole: partner.planningRole || '',
        mainWeddingDayContact: partner.mainWeddingDayContact || false,
        emergencyContact: {
          name: partner.emergencyContact?.name || '',
          phone: partner.emergencyContact?.phone || '',
          relationship: partner.emergencyContact?.relationship || ''
        },
        dietaryPreferences: partner.dietaryPreferences || '',
        allergies: partner.allergies || '',
        accessibilityNeeds: partner.accessibilityNeeds || ''
      });
    } catch (err) {
      setError('Failed to load partner details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmergencyChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      emergencyContact: { ...prev.emergencyContact, [field]: e.target.value }
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      const partnersData = {
        partners: {
          [partnerRef]: {
            planningRole: formData.planningRole,
            mainWeddingDayContact: formData.mainWeddingDayContact,
            emergencyContact: formData.emergencyContact,
            dietaryPreferences: formData.dietaryPreferences,
            allergies: formData.allergies,
            accessibilityNeeds: formData.accessibilityNeeds
          }
        }
      };
      await api.put('/api/wedding-details/partners', partnersData);
      setSuccess('Saved successfully');
      onSaved?.();
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 800);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = () => {
    const name = formData.fullName || formData.preferredName || guest?.name || '';
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!guest) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={formData.profilePicture}
            sx={{ width: 40, height: 40, bgcolor: partnerRef === 'partnerOne' ? '#E91E63' : '#2196F3' }}
          >
            {getInitials()}
          </Avatar>
          <Typography variant="h6">
            {formData.fullName || formData.preferredName || guest?.name || (partnerRef === 'partnerOne' ? 'Partner 1' : 'Partner 2')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ ...styles.dialogContent }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info size={16} />
              Planning Role
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Planning Role</InputLabel>
              <Select
                value={formData.planningRole}
                label="Planning Role"
                onChange={handleChange('planningRole')}
              >
                {planningRoles.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.mainWeddingDayContact}
                  onChange={handleChange('mainWeddingDayContact')}
                />
              }
              label="Main contact on wedding day"
            />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Carrot size={16} />
              Dietary & Accessibility
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Dietary Preferences"
                  value={formData.dietaryPreferences}
                  onChange={handleChange('dietaryPreferences')}
                  placeholder="e.g., vegetarian, gluten-free"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Allergies"
                  value={formData.allergies}
                  onChange={handleChange('allergies')}
                  placeholder="List any allergies"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Accessibility Needs"
                  value={formData.accessibilityNeeds}
                  onChange={handleChange('accessibilityNeeds')}
                  placeholder="e.g., mobility assistance"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
              Emergency Contact
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contact Name"
                  value={formData.emergencyContact.name}
                  onChange={handleEmergencyChange('name')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone"
                  value={formData.emergencyContact.phone}
                  onChange={handleEmergencyChange('phone')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Relationship</InputLabel>
                  <Select
                    value={formData.emergencyContact.relationship}
                    label="Relationship"
                    onChange={handleEmergencyChange('relationship')}
                  >
                    {relationships.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving || loading}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CoupleMemberGuestModal;
